import { useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { apiUrl } from "@/lib/api-config";
import {
  postContractReviewSse,
  type ContractReviewSseCallbacks,
  type ReviewStreamCompletion,
} from "@/lib/contract-review-sse";
import { readSupportingDocumentsAsBase64 } from "@/lib/supporting-documents-payload";
import type { ReviewFormData } from "@/types";

/**
 * Re-run contract review for an existing saved review (metadata only; server uses stored contract text).
 */
export function useEditReview() {
  const { getAccessToken } = useAuth();

  const regenerate = useCallback(
    async (
      reviewId: string,
      formData: ReviewFormData,
      sseCallbacks?: ContractReviewSseCallbacks,
    ): Promise<ReviewStreamCompletion | null> => {
      const supportingDocuments = await readSupportingDocumentsAsBase64(
        formData.supportingDocs ?? []
      );

      const requestData = {
        backgroundInfo: formData.backgroundInfo || "No background information provided.",
        agentInstructions: formData.agentInstructions || "",
        contractType: formData.contractType,
        userRole: formData.userRole,
        jurisdiction: formData.jurisdiction,
        skipResearch: formData.skipResearch !== false,
        ...(supportingDocuments.length > 0 ? { supportingDocuments } : {}),
      };

      const accessToken = await getAccessToken();
      const url = apiUrl(`/review-contract/${encodeURIComponent(reviewId)}/regenerate`);
      return postContractReviewSse(url, requestData, accessToken, sseCallbacks);
    },
    [getAccessToken],
  );

  return { regenerate };
}
